/** Same-origin CSM envelope client. Tokens are never read by or stored in JavaScript. */
export class ApiError extends Error {
  constructor(code, message, status = 0, retryable = false) {
    super(message); this.name = 'ApiError'; this.code = code; this.status = status; this.retryable = retryable;
  }
}
export const operationId = (prefix = 'op') => `${prefix}-${globalThis.crypto.randomUUID()}`;
export class ApiClient {
  constructor({ request = globalThis.fetch.bind(globalThis), onUnauthorized = () => {}, timeoutMs = 15000 } = {}) {
    this.request = request; this.onUnauthorized = onUnauthorized; this.timeoutMs = timeoutMs;
    this.epoch = 0; this.inflight = new Set();
  }
  invalidate() {
    this.epoch += 1;
    for (const c of this.inflight) c.abort();
    this.inflight.clear();
  }
  async call(path, { method = 'GET', body, key, signal, timeoutMs = this.timeoutMs } = {}) {
    if (!/^\/api\/[a-z0-9/?=&_.%-]+$/i.test(path) || path.includes('..') || /%2f|%5c/i.test(path))
      throw new ApiError('UNSAFE_PATH', 'Geçersiz API yolu.');
    const epoch = this.epoch, controller = new AbortController();
    this.inflight.add(controller);
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const abort = () => controller.abort();
    signal?.addEventListener('abort', abort, { once: true });
    if (signal?.aborted) controller.abort();
    try {
      const headers = { accept: 'application/json' };
      if (body !== undefined) headers['content-type'] = 'application/json';
      if (key) headers['idempotency-key'] = key;
      const response = await this.request(path, {
        method, headers, credentials: 'same-origin', cache: 'no-store', redirect: 'error',
        body: body === undefined ? undefined : JSON.stringify(body), signal: controller.signal
      });
      if (epoch !== this.epoch) throw new ApiError('STALE_SESSION', 'Hesap değişti.');
      const type = response.headers.get('content-type') || '';
      if (!type.includes('application/json')) throw new ApiError('INVALID_RESPONSE', 'Sunucu cevabı beklenen biçimde değil.', response.status);
      const raw = await response.text();
      if (raw.length > 4_000_000) throw new ApiError('RESPONSE_TOO_LARGE', 'Cevap sınırı aşıldı.', response.status);
      let value;
      try { value = JSON.parse(raw); } catch { throw new ApiError('INVALID_RESPONSE', 'Sunucu cevabı okunamadı.', response.status); }
      if (epoch !== this.epoch) throw new ApiError('STALE_SESSION', 'Hesap değişti.');
      if (!response.ok) {
        if (response.status === 401) this.onUnauthorized();
        throw new ApiError(value?.error?.code || 'HTTP_ERROR', value?.error?.message || 'İşlem tamamlanamadı.', response.status, response.status >= 500 || response.status === 429);
      }
      if (!value || !Object.prototype.hasOwnProperty.call(value, 'data')) throw new ApiError('INVALID_ENVELOPE', 'Sunucu cevap sözleşmesi geçersiz.');
      return value.data;
    } catch (e) {
      if (epoch !== this.epoch) throw new ApiError('STALE_SESSION', 'Hesap değişti.');
      if (e instanceof ApiError) throw e;
      throw new ApiError(controller.signal.aborted ? 'TIMEOUT' : 'NETWORK', controller.signal.aborted ? 'İşlem zaman aşımına uğradı. Sonucu tekrar sorgulayın.' : 'Bağlantı kurulamadı. Yerel kayıtlar korunuyor.', 0, true);
    } finally {
      clearTimeout(timer); signal?.removeEventListener('abort', abort); this.inflight.delete(controller);
    }
  }
}
/** A payment return URL is not proof of payment. This only authorizes navigation. */
export function safeCheckoutUrl(raw, origin, allowedOrigins = []) {
  const u = new URL(raw, origin), own = new URL(origin);
  if (u.username || u.password || u.hash) throw new ApiError('UNSAFE_CHECKOUT', 'Ödeme bağlantısı doğrulanamadı.');
  if (u.origin === own.origin && /^\/qa-payment\//.test(u.pathname) && ['127.0.0.1', 'localhost'].includes(own.hostname)) return u.href;
  if (u.protocol !== 'https:' || !allowedOrigins.includes(u.origin)) throw new ApiError('UNSAFE_CHECKOUT', 'Ödeme sağlayıcısı adresi sunucu yapılandırmasında izinli değil.');
  return u.href;
}
